import React from 'react';
import styled from 'styled-components';

// Styled Components
const FeaturesSection = styled.section`
  padding: 100px 0;
  background-color: var(--bg-white);
`;

const SectionHeader = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 60px;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  color: var(--text-dark);
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionDescription = styled.p`
  font-size: 1.1rem;
  color: var(--text-light);
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background-color: var(--bg-white);
  border-radius: 12px;
  padding: 2rem;
  box-shadow: var(--shadow-sm);
  transition: all var(--transition-normal);
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: var(--shadow-lg);
  }
`;

const FeatureIconWrapper = styled.div`
  width: 70px;
  height: 70px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--primary-light);
  background-opacity: 0.1;
  border-radius: 12px;
  margin-bottom: 1.5rem;
`;

const FeatureTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: var(--text-dark);
`;

const FeatureDescription = styled.p`
  color: var(--text-light);
  line-height: 1.6;
`;

// SVG Icons for Each Feature
const CustomizationIcon = () => (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M20 11.6667C22.7614 11.6667 25 9.42809 25 6.66667C25 3.90524 22.7614 1.66667 20 1.66667C17.2386 1.66667 15 3.90524 15 6.66667C15 9.42809 17.2386 11.6667 20 11.6667Z" stroke="#4338CA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M20 38.3333V11.6667" stroke="#4338CA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M31.6667 20C34.428 20 36.6667 22.2386 36.6667 25C36.6667 27.7614 34.428 30 31.6667 30C28.9053 30 26.6667 27.7614 26.6667 25C26.6667 22.2386 28.9053 20 31.6667 20Z" stroke="#4338CA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M3.33337 25H26.6667" stroke="#4338CA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M8.33337 38.3333C11.0948 38.3333 13.3334 36.0948 13.3334 33.3333C13.3334 30.5719 11.0948 28.3333 8.33337 28.3333C5.57195 28.3333 3.33337 30.5719 3.33337 33.3333C3.33337 36.0948 5.57195 38.3333 8.33337 38.3333Z" stroke="#4338CA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M38.3334 33.3333H13.3334" stroke="#4338CA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const IntuitiveShoppingIcon = () => (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M13.3334 36.6667C14.2538 36.6667 15 35.9205 15 35C15 34.0795 14.2538 33.3333 13.3334 33.3333C12.4129 33.3333 11.6667 34.0795 11.6667 35C11.6667 35.9205 12.4129 36.6667 13.3334 36.6667Z" stroke="#4338CA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M30 36.6667C30.9205 36.6667 31.6667 35.9205 31.6667 35C31.6667 34.0795 30.9205 33.3333 30 33.3333C29.0795 33.3333 28.3334 34.0795 28.3334 35C28.3334 35.9205 29.0795 36.6667 30 36.6667Z" stroke="#4338CA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M1.66675 1.66667H8.33342L12.8334 24.5833C13.0146 25.5309 13.5372 26.3867 14.3059 26.9876C15.0745 27.5886 16.0388 27.893 17.0001 27.8333H26.4167C27.3781 27.893 28.3423 27.5886 29.111 26.9876C29.8796 26.3867 30.4022 25.5309 30.5834 24.5833L33.3334 10H10.0001" stroke="#4338CA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M20 13.3333L20 20" stroke="#4338CA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M16.6667 16.6667L23.3334 16.6667" stroke="#4338CA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const PaymentIcon = () => (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="3.33337" y="8.33333" width="33.3333" height="23.3333" rx="2" stroke="#4338CA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M3.33337 16.6667H36.6667" stroke="#4338CA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M10 25H16.6667" stroke="#4338CA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="26.6667" cy="25" r="3.33333" stroke="#4338CA" strokeWidth="2" />
    </svg>
);

const AnalyticsIcon = () => (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M36.6666 33.3333H3.33325V6.66667" stroke="#4338CA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M11.6667 25L18.3334 18.3333L25.0001 25L36.6667 13.3333" stroke="#4338CA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="36.6667" cy="13.3333" r="3.33333" stroke="#4338CA" strokeWidth="2" />
    </svg>
);

const InventoryIcon = () => (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M30.0001 16.6667V36.6667M10.0001 36.6667V16.6667" stroke="#4338CA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M36.6667 36.6667H3.33337" stroke="#4338CA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M36.6667 10H3.33337" stroke="#4338CA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M35 10L20 3.33333L5 10" stroke="#4338CA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <rect x="16.6667" y="16.6667" width="6.66667" height="6.66667" stroke="#4338CA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <rect x="16.6667" y="26.6667" width="6.66667" height="6.66667" stroke="#4338CA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const NotificationIcon = () => (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M30 13.3333C30 10.6812 28.9464 8.13762 27.0711 6.26227C25.1957 4.38691 22.6522 3.33333 20 3.33333C17.3478 3.33333 14.8043 4.38691 12.9289 6.26227C11.0536 8.13762 10 10.6812 10 13.3333C10 25 5 28.3333 5 28.3333H35C35 28.3333 30 25 30 13.3333Z" stroke="#4338CA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M22.8834 35C22.5634 35.5883 22.0954 36.0749 21.5266 36.4052C20.9579 36.7355 20.3103 36.8949 19.65 36.8649C18.9898 36.8949 18.3422 36.7355 17.7734 36.4052C17.2046 36.0749 16.7366 35.5883 16.4167 35" stroke="#4338CA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="30" cy="10" r="5" fill="#F59E0B" />
    </svg>
);

// Features Component
const Features = () => {
    // Features Data
    const features = [
        {
            id: 1,
            icon: <CustomizationIcon />,
            title: "客製化品牌體驗",
            description: "完全依照您的品牌調整介面設計、色彩和風格，創造獨特的購物體驗。"
        },
        {
            id: 2,
            icon: <IntuitiveShoppingIcon />,
            title: "直覺的購物流程",
            description: "優化的用戶介面設計，讓購物流程更加順暢，提高轉換率和客戶滿意度。"
        },
        {
            id: 3,
            icon: <PaymentIcon />,
            title: "多元支付整合",
            description: "支援信用卡、行動支付等多種付款方式，滿足不同客戶的需求。"
        },
        {
            id: 4,
            icon: <AnalyticsIcon />,
            title: "強大的分析工具",
            description: "深入了解客戶行為和銷售數據，幫助您做出更明智的商業決策。"
        },
        {
            id: 5,
            icon: <InventoryIcon />,
            title: "庫存與訂單管理",
            description: "簡化庫存追蹤和訂單處理，即時更新庫存狀態，提升營運效率。"
        },
        {
            id: 6,
            icon: <NotificationIcon />,
            title: "推播通知",
            description: "透過即時通知保持與客戶的聯繫，推廣新品和特別優惠，增加顧客回購率。"
        }
    ];

    return (
        <FeaturesSection id="features">
            <div className="container">
                <SectionHeader>
                    <SectionTitle>專為商家設計的全方位功能</SectionTitle>
                    <SectionDescription>
                        不只是電商平台，ShopWave 提供完整的行動商務解決方案，助您的業務快速成長
                    </SectionDescription>
                </SectionHeader>

                <FeaturesGrid>
                    {features.map(feature => (
                        <FeatureCard key={feature.id}>
                            <FeatureIconWrapper>
                                {feature.icon}
                            </FeatureIconWrapper>
                            <FeatureTitle>{feature.title}</FeatureTitle>
                            <FeatureDescription>{feature.description}</FeatureDescription>
                        </FeatureCard>
                    ))}
                </FeaturesGrid>
            </div>
        </FeaturesSection>
    );
};

export default Features;